import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FaNewspaper, FaVideo, FaPlus, FaTrash, FaExternalLinkAlt, FaSave, FaUpload, FaPlayCircle, FaEdit } from 'react-icons/fa';

interface NewsMediaItem {
  id: number;
  title: string;
  category?: string;
  summary?: string;
  image?: string;
  source_link?: string;
  date?: string;
}

const NewsMediaAdmin: React.FC = () => {
  const [activeSubTab, setActiveSubTab] = useState<'NEWS' | 'VIDEO'>('NEWS');
  const [newsList, setNewsList] = useState<NewsMediaItem[]>([]);
  const [loading, setLoading] = useState(true);

  // New Media Form
  const [showAddModal, setShowAddModal] = useState(false);
  const [newItem, setNewItem] = useState({
    title: '',
    category: 'Media Coverage',
    summary: '',
    image: '',
    source_link: '',
    date: new Date().toISOString().split('T')[0]
  });
  const [isUploading, setIsUploading] = useState(false);

  // Edit Media Form State
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState<NewsMediaItem | null>(null);

  // Featured Video Form State
  const [videoForm, setVideoForm] = useState({
    title: 'Discover Rotary Means Business Fellowship Jaipur - Official Introduction',
    video_url: 'https://www.youtube.com/embed/LXb3EKWsInQ',
    description: 'Watch how Rotary Means Business Jaipur fosters high-trust business networking, cross-referred commerce, and fellowship among Rotarian business leaders.'
  });
  const [savingVideo, setSavingVideo] = useState(false);

  useEffect(() => {
    fetchNewsMedia();
    fetchFeaturedVideo();
  }, []);

  const fetchNewsMedia = async () => {
    setLoading(true);
    try {
      const res = await axios.get('https://rmbbackend.rmbjaipur.com/auth/news-media');
      setNewsList(res.data.data || []);
    } catch (err) {
      console.error('Error fetching news media:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchFeaturedVideo = async () => {
    try {
      const res = await axios.get('https://rmbbackend.rmbjaipur.com/auth/featured-video');
      if (res.data?.data) {
        setVideoForm({
          title: res.data.data.title || '',
          video_url: res.data.data.video_url || '',
          description: res.data.data.description || ''
        });
      }
    } catch (err) {
      console.error('Error fetching featured video:', err);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);
    setIsUploading(true);

    try {
      const res = await axios.post('https://rmbbackend.rmbjaipur.com/auth/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setNewItem(prev => ({ ...prev, image: res.data.imagePath }));
    } catch (err) {
      console.error('Upload error:', err);
      alert('Failed to upload image');
    } finally {
      setIsUploading(false);
    }
  };

  const handleCreateNews = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newItem.title.trim()) return alert('Please enter article title');

    try {
      await axios.post('https://rmbbackend.rmbjaipur.com/auth/news-media', newItem);
      alert('📰 RMB News & Media article added successfully!');
      setShowAddModal(false);
      setNewItem({
        title: '',
        category: 'Media Coverage',
        summary: '',
        image: '',
        source_link: '',
        date: new Date().toISOString().split('T')[0]
      });
      fetchNewsMedia();
    } catch (err) {
      console.error('Error creating news item:', err);
      alert('Failed to add news media article');
    }
  };

  const handleEditFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !editingItem) return;

    const formData = new FormData();
    formData.append('image', file);
    setIsUploading(true);

    try {
      const res = await axios.post('https://rmbbackend.rmbjaipur.com/auth/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setEditingItem(prev => (prev ? { ...prev, image: res.data.imagePath } : null));
    } catch (err) {
      console.error('Upload error:', err);
      alert('Failed to upload image');
    } finally {
      setIsUploading(false);
    }
  };

  const handleOpenEditModal = (item: NewsMediaItem) => {
    setEditingItem({ ...item });
    setShowEditModal(true);
  };

  const handleUpdateNews = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingItem || !editingItem.title.trim()) return alert('Please enter article title');

    try {
      await axios.put(`https://rmbbackend.rmbjaipur.com/auth/news-media/${editingItem.id}`, editingItem);
      alert('📰 RMB News & Media article updated successfully!');
      setShowEditModal(false);
      setEditingItem(null);
      fetchNewsMedia();
    } catch (err) {
      console.error('Error updating news item:', err);
      alert('Failed to update news media article');
    }
  };

  const handleDeleteNews = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this news article?')) return;
    try {
      await axios.delete(`https://rmbbackend.rmbjaipur.com/auth/news-media/${id}`);
      alert('Article deleted');
      fetchNewsMedia();
    } catch (err) {
      console.error('Error deleting news article:', err);
      alert('Failed to delete article');
    }
  };

  const handleSaveVideo = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingVideo(true);
    try {
      await axios.post('https://rmbbackend.rmbjaipur.com/auth/featured-video', videoForm);
      alert('🎬 Featured Video settings updated successfully!');
      fetchFeaturedVideo();
    } catch (err) {
      console.error('Error updating video:', err);
      alert('Failed to update featured video');
    } finally {
      setSavingVideo(false);
    }
  };

  return (
    <div className="p-6 bg-slate-900 min-h-screen text-slate-100 space-y-6">
      {/* Header */}
      <div className="bg-[#1E2B45] p-5 rounded-2xl border border-blue-500/40 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 shadow-xl">
        <div>
          <h1 className="text-2xl font-black text-white flex items-center gap-2">
            <FaNewspaper className="text-[#FDBC12]" /> RMB News & Media & Featured Video Management
          </h1>
          <p className="text-xs text-slate-400 mt-1">
            Manage press coverage, media highlights, news articles, and chapter featured video displayed on the home page.
          </p>
        </div>

        {/* Tab Selector */}
        <div className="flex bg-[#131E32] p-1.5 rounded-xl border border-[#2A3B5C]">
          <button
            onClick={() => setActiveSubTab('NEWS')}
            className={`px-4 py-2 rounded-lg text-xs font-bold transition flex items-center gap-2 cursor-pointer ${activeSubTab === 'NEWS' ? 'bg-[#FDBC12] text-[#0A2451] font-black' : 'text-slate-300 hover:text-white'
              }`}
          >
            <FaNewspaper /> News & Media Articles ({newsList.length})
          </button>
          <button
            onClick={() => setActiveSubTab('VIDEO')}
            className={`px-4 py-2 rounded-lg text-xs font-bold transition flex items-center gap-2 cursor-pointer ${activeSubTab === 'VIDEO' ? 'bg-[#FDBC12] text-[#0A2451] font-black' : 'text-slate-300 hover:text-white'
              }`}
          >
            <FaVideo /> Featured Chapter Video
          </button>
        </div>
      </div>

      {/* SUB-TAB 1: NEWS & MEDIA ARTICLES */}
      {activeSubTab === 'NEWS' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center bg-[#1E2B45] p-4 rounded-xl border border-[#2A3B5C]">
            <h2 className="text-sm font-extrabold text-amber-300 uppercase tracking-wider flex items-center gap-2">
              📰 Active News & Media Articles
            </h2>
            <button
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 bg-[#FDBC12] hover:bg-amber-400 text-[#0A2451] font-black text-xs rounded-lg shadow flex items-center gap-1.5 cursor-pointer"
            >
              <FaPlus /> Add Media Article
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {newsList.map(item => (
              <div key={item.id} className="bg-[#1E2B45] rounded-xl border border-[#2A3B5C] overflow-hidden flex flex-col justify-between shadow-lg">
                {item.image ? (
                  <img
                    src={item.image.startsWith('http') ? item.image : `https://rmbbackend.rmbjaipur.com/${item.image.replace(/^\//, '')}`}
                    alt={item.title}
                    className="h-44 w-full object-cover"
                  />
                ) : (
                  <div className="h-44 w-full bg-slate-800 flex items-center justify-center text-slate-500 font-bold text-xs">
                    No Media Image Attached
                  </div>
                )}

                <div className="p-4 space-y-2 flex-1 flex flex-col justify-between">
                  <div>
                    <div className="flex justify-between items-center text-[11px] font-bold text-[#FDBC12] mb-1">
                      <span>{item.category || 'Media Coverage'}</span>
                      <span className="text-slate-400">{item.date}</span>
                    </div>
                    <h3 className="font-bold text-white text-sm leading-snug line-clamp-2">{item.title}</h3>
                    <p className="text-slate-300 text-xs mt-1 leading-relaxed line-clamp-3">{item.summary}</p>
                  </div>

                  <div className="flex justify-between items-center pt-3 border-t border-[#2A3B5C] mt-2">
                    {item.source_link ? (
                      <a
                        href={item.source_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-400 hover:text-blue-300 text-xs font-bold flex items-center gap-1"
                      >
                        Read Link <FaExternalLinkAlt size={10} />
                      </a>
                    ) : <span />}

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleOpenEditModal(item)}
                        className="px-3 py-1 bg-amber-500/20 hover:bg-amber-500 text-amber-300 hover:text-slate-950 font-bold text-xs rounded-lg border border-amber-500/40 flex items-center gap-1 cursor-pointer transition"
                      >
                        <FaEdit size={12} /> Edit
                      </button>
                      <button
                        onClick={() => handleDeleteNews(item.id)}
                        className="px-3 py-1 bg-red-600/30 hover:bg-red-600 text-red-300 hover:text-white font-bold text-xs rounded-lg border border-red-500/40 flex items-center gap-1 cursor-pointer transition"
                      >
                        <FaTrash size={12} /> Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SUB-TAB 2: FEATURED CHAPTER VIDEO CONFIGURATION */}
      {activeSubTab === 'VIDEO' && (
        <div className="bg-[#1E2B45] p-6 rounded-2xl border border-blue-500/40 shadow-2xl space-y-6 max-w-3xl mx-auto">
          <h2 className="text-base font-black text-amber-300 border-b border-[#2A3B5C] pb-3 flex items-center gap-2 uppercase tracking-wider">
            🎬 Configure Homepage Featured Video Section
          </h2>

          <form onSubmit={handleSaveVideo} className="space-y-4 text-xs">
            <div>
              <label className="font-bold text-slate-300 block mb-1">Video Title *</label>
              <input
                type="text"
                value={videoForm.title}
                onChange={(e) => setVideoForm({ ...videoForm, title: e.target.value })}
                placeholder="e.g. Discover Rotary Means Business Fellowship Jaipur"
                className="w-full p-3 bg-[#131E32] border border-[#2A3B5C] rounded-lg text-white font-bold outline-none"
                required
              />
            </div>

            <div>
              <label className="font-bold text-slate-300 block mb-1">YouTube Video URL / Embed Link *</label>
              <input
                type="text"
                value={videoForm.video_url}
                onChange={(e) => setVideoForm({ ...videoForm, video_url: e.target.value })}
                placeholder="e.g. https://www.youtube.com/watch?v=... or https://www.youtube.com/embed/..."
                className="w-full p-3 bg-[#131E32] border border-[#2A3B5C] rounded-lg text-white font-mono text-xs outline-none"
                required
              />
              <p className="text-[11px] text-slate-400 mt-1">Supports standard YouTube URLs (`watch?v=...`) or direct embed links.</p>
            </div>

            <div>
              <label className="font-bold text-slate-300 block mb-1">Video Description</label>
              <textarea
                value={videoForm.description}
                onChange={(e) => setVideoForm({ ...videoForm, description: e.target.value })}
                rows={3}
                placeholder="Brief summary of the featured video content..."
                className="w-full p-3 bg-[#131E32] border border-[#2A3B5C] rounded-lg text-white font-medium outline-none"
              />
            </div>

            {/* Video Preview */}
            {videoForm.video_url && (
              <div className="bg-[#131E32] p-4 rounded-xl border border-[#2A3B5C] space-y-2">
                <span className="font-bold text-emerald-400 text-xs flex items-center gap-1.5">
                  <FaPlayCircle /> Live Video Stream Preview:
                </span>
                <div className="relative aspect-video rounded-xl overflow-hidden border border-slate-700 shadow-xl">
                  <iframe
                    src={videoForm.video_url.includes('watch?v=') ? videoForm.video_url.replace('watch?v=', 'embed/') : videoForm.video_url}
                    title="Featured Video Preview"
                    className="w-full h-full border-0"
                    allowFullScreen
                  />
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={savingVideo}
              className="px-6 py-3 bg-[#FDBC12] hover:bg-amber-400 text-[#0A2451] font-black text-xs rounded-xl shadow-lg flex items-center gap-2 cursor-pointer"
            >
              <FaSave /> {savingVideo ? 'Saving Settings...' : 'Save Featured Video Settings'}
            </button>
          </form>
        </div>
      )}

      {/* NEW ARTICLE MODAL */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-[#1E2B45] rounded-2xl border border-[#2A3B5C] shadow-2xl w-full max-w-lg p-6 relative text-slate-100 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-black text-white border-b border-[#2A3B5C] pb-3 mb-4">
              ➕ Add New RMB News & Media Article
            </h3>

            <form onSubmit={handleCreateNews} className="space-y-4 text-xs">
              <div>
                <label className="font-bold text-slate-300 block mb-1">Article Title *</label>
                <input
                  type="text"
                  value={newItem.title}
                  onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                  placeholder="e.g. RMB Jaipur Expands Business Referral Network"
                  className="w-full p-2.5 bg-[#131E32] border border-[#2A3B5C] rounded-lg text-white font-bold outline-none"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-bold text-slate-300 block mb-1">Category</label>
                  <input
                    type="text"
                    value={newItem.category}
                    onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                    placeholder="e.g. Media Coverage, Press Release"
                    className="w-full p-2.5 bg-[#131E32] border border-[#2A3B5C] rounded-lg text-white outline-none"
                  />
                </div>
                <div>
                  <label className="font-bold text-slate-300 block mb-1">Publication Date</label>
                  <input
                    type="text"
                    value={newItem.date}
                    onChange={(e) => setNewItem({ ...newItem, date: e.target.value })}
                    placeholder="e.g. July 25, 2026"
                    className="w-full p-2.5 bg-[#131E32] border border-[#2A3B5C] rounded-lg text-white outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="font-bold text-slate-300 block mb-1">Article Summary</label>
                <textarea
                  value={newItem.summary}
                  onChange={(e) => setNewItem({ ...newItem, summary: e.target.value })}
                  rows={3}
                  placeholder="Brief summary of the media coverage or press release..."
                  className="w-full p-2.5 bg-[#131E32] border border-[#2A3B5C] rounded-lg text-white outline-none"
                />
              </div>

              <div>
                <label className="font-bold text-slate-300 block mb-1">External News Link (Optional)</label>
                <input
                  type="url"
                  value={newItem.source_link}
                  onChange={(e) => setNewItem({ ...newItem, source_link: e.target.value })}
                  placeholder="https://..."
                  className="w-full p-2.5 bg-[#131E32] border border-[#2A3B5C] rounded-lg text-white outline-none"
                />
              </div>

              {/* Upload Image */}
              <div>
                <label className="font-bold text-slate-300 block mb-1">Cover Image / Media Clipping</label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={newItem.image}
                    onChange={(e) => setNewItem({ ...newItem, image: e.target.value })}
                    placeholder="Image URL or upload..."
                    className="flex-1 p-2 bg-[#131E32] border border-[#2A3B5C] rounded-lg text-white text-xs outline-none"
                  />
                  <label className="px-3 py-2 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-lg cursor-pointer flex items-center gap-1.5">
                    <FaUpload /> {isUploading ? 'Uploading...' : 'Upload'}
                    <input type="file" accept="image/*" onChange={handleFileUpload} className="hidden" />
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-[#2A3B5C]">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border border-gray-600 text-gray-300 font-bold rounded-xl cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-[#FDBC12] hover:bg-amber-400 text-[#0A2451] font-black rounded-xl shadow cursor-pointer"
                >
                  Save Media Article
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* EDIT ARTICLE MODAL */}
      {showEditModal && editingItem && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-[#1E2B45] rounded-2xl border border-[#2A3B5C] shadow-2xl w-full max-w-lg p-6 relative text-slate-100 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-black text-amber-300 border-b border-[#2A3B5C] pb-3 mb-4 flex items-center gap-2">
              ✏️ Edit RMB News & Media Article (ID: #{editingItem.id})
            </h3>

            <form onSubmit={handleUpdateNews} className="space-y-4 text-xs">
              <div>
                <label className="font-bold text-slate-300 block mb-1">Article Title *</label>
                <input
                  type="text"
                  value={editingItem.title}
                  onChange={(e) => setEditingItem({ ...editingItem, title: e.target.value })}
                  placeholder="Article Title..."
                  className="w-full p-2.5 bg-[#131E32] border border-[#2A3B5C] rounded-lg text-white font-bold outline-none"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-bold text-slate-300 block mb-1">Category</label>
                  <input
                    type="text"
                    value={editingItem.category || ''}
                    onChange={(e) => setEditingItem({ ...editingItem, category: e.target.value })}
                    placeholder="e.g. Media Coverage, Press Release"
                    className="w-full p-2.5 bg-[#131E32] border border-[#2A3B5C] rounded-lg text-white outline-none"
                  />
                </div>
                <div>
                  <label className="font-bold text-slate-300 block mb-1">Publication Date</label>
                  <input
                    type="text"
                    value={editingItem.date || ''}
                    onChange={(e) => setEditingItem({ ...editingItem, date: e.target.value })}
                    placeholder="e.g. July 25, 2026"
                    className="w-full p-2.5 bg-[#131E32] border border-[#2A3B5C] rounded-lg text-white outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="font-bold text-slate-300 block mb-1">Article Summary / Content</label>
                <textarea
                  value={editingItem.summary || ''}
                  onChange={(e) => setEditingItem({ ...editingItem, summary: e.target.value })}
                  rows={4}
                  placeholder="Summary or complete article text..."
                  className="w-full p-2.5 bg-[#131E32] border border-[#2A3B5C] rounded-lg text-white outline-none"
                />
              </div>

              <div>
                <label className="font-bold text-slate-300 block mb-1">External Media Link (Optional)</label>
                <input
                  type="url"
                  value={editingItem.source_link || ''}
                  onChange={(e) => setEditingItem({ ...editingItem, source_link: e.target.value })}
                  placeholder="https://..."
                  className="w-full p-2.5 bg-[#131E32] border border-[#2A3B5C] rounded-lg text-white outline-none"
                />
              </div>

              {/* Upload Image */}
              <div>
                <label className="font-bold text-slate-300 block mb-1">Cover Image / Media Clipping</label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={editingItem.image || ''}
                    onChange={(e) => setEditingItem({ ...editingItem, image: e.target.value })}
                    placeholder="Image URL or upload..."
                    className="flex-1 p-2 bg-[#131E32] border border-[#2A3B5C] rounded-lg text-white text-xs outline-none"
                  />
                  <label className="px-3 py-2 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-lg cursor-pointer flex items-center gap-1.5">
                    <FaUpload /> {isUploading ? 'Uploading...' : 'Upload'}
                    <input type="file" accept="image/*" onChange={handleEditFileUpload} className="hidden" />
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-[#2A3B5C]">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 border border-gray-600 text-gray-300 font-bold rounded-xl cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-500 hover:to-amber-600 text-[#0A2451] font-black rounded-xl shadow cursor-pointer transition"
                >
                  Update Article
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsMediaAdmin;
